import React, { useEffect, useMemo, useState } from "react";
import { Button, Alert, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { useGetUser } from "../api/queries/useGetUser";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import ProfileModal from "./common/ProfileModal";
import { User } from "../types/Types";
import CurvedTextOverlay from "./Account/CurvedTextOverlay";
import { FaUniversity } from "react-icons/fa";
import { MdOutlineBusiness } from "react-icons/md";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../api/queries/useTranslateDynamicFields";
import {
  isEarlyStageMembership,
  isLegacyYouthMembership,
  isYouthOrEarlyStageMembership,
} from "../utils/membershipTier";

const formatNamePart = (name: string) =>
  name ? name.charAt(0).toUpperCase() + name.slice(1) : "";

const DASHBOARD_CARD_BTN_CLASS =
  "btn w-100 mt-2 fs-6 fs-md-5 text-wrap lh-sm px-2 px-md-3";

const cardData = [
  {
    titleKey: "dashboard.cards.profile.title",
    textKey: "dashboard.cards.profile.text",
    link: "",
    logo: "/userIcon.png",
    isComingSoon: false,
  },
  {
    titleKey: "dashboard.cards.connections.title",
    textKey: "dashboard.cards.connections.text",
    link: "/connections",
    logo: "/connect.png",
    isComingSoon: false,
  },
  {
    titleKey: "dashboard.cards.messages.title",
    textKey: "dashboard.cards.messages.text",
    link: "/messages",
    logo: "/message.png",
    isComingSoon: false,
  },
  {
    titleKey: "dashboard.cards.muslimInvest.title",
    textKey: "dashboard.cards.muslimInvest.text",
    link: "/investing",
    logo: "/MI.png",
    isComingSoon: false,
  },
  {
    titleKey: "dashboard.cards.internationalMarkets.title",
    textKey: "dashboard.cards.internationalMarkets.text",
    link: "/international-markets",
    logo: "/MIM.png",
    isComingSoon: true,
  },
  {
    titleKey: "dashboard.cards.ventureBuilder.title",
    textKey: "dashboard.cards.ventureBuilder.text",
    link: "/venture-builder",
    logo: "/MVB.png",
    isComingSoon: true,
  },
];

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const { setUser, objectId } = useAppContext();
  const {
    data: userData,
    isPending,
    isFetching,
  } = useGetUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isLoggedIn() && !!objectId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData, setUser]);

  const selectedUser = { ...userData?.user, ...userData?.imageUrls };
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const imageSize = isDesktop ? 150 : isLgTablet ? 140 : isMdTablet ? 130 : 95;

  const dashboardFullName = useMemo(() => {
    if (!userData?.user) return "";
    const first = formatNamePart(userData.user.firstName || "");
    const last = formatNamePart(userData.user.lastName || "");
    return `${first} ${last}`.trim();
  }, [userData?.user]);

  const dashboardRoleLine = useMemo(() => {
    const u = userData?.user;
    if (!u) return "";
    if (
      u.membershipName === "Youth Membership" ||
      u.membershipName === "Early Stage Membership"
    ) {
      return u.studyInterest ? formatNamePart(u.studyInterest) : "";
    }
    return u.personalPositionCompany
      ? formatNamePart(u.personalPositionCompany)
      : "";
  }, [userData?.user]);

  const dashboardSummaryFields = useMemo(() => {
    const u = userData?.user;
    if (!u) return {};
    const fields: Record<string, string> = {};
    if (dashboardFullName) fields.fullName = dashboardFullName;
    if (dashboardRoleLine) fields.roleLine = dashboardRoleLine;
    const sector = u.businessSectorIndustry?.trim();
    if (sector) fields.sector = sector;
    const businessName = u.businessName?.trim();
    if (businessName) fields.businessName = businessName;
    return fields;
  }, [userData?.user, dashboardFullName, dashboardRoleLine]);

  const { data: translatedDashboardSummary } = useTranslateDynamicFields(
    dashboardSummaryFields,
    i18n.resolvedLanguage || i18n.language
  );

  const displayDashboardName =
    translatedDashboardSummary?.fullName?.trim() || dashboardFullName;
  const displayDashboardRole =
    translatedDashboardSummary?.roleLine?.trim() || dashboardRoleLine;
  const displayDashboardSector =
    translatedDashboardSummary?.sector?.trim() ||
    userData?.user?.businessSectorIndustry ||
    "";
  const displayDashboardBusinessName =
    translatedDashboardSummary?.businessName?.trim() ||
    userData?.user?.businessName ||
    "";
  const isEarlyStageMember = isEarlyStageMembership(selectedUser?.membershipName);
  const isLegacyYouthMember = isLegacyYouthMembership(selectedUser?.membershipName);

  if (!objectId || isPending || isFetching || !userData?.user) {
    return (
      <div className="container p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("dashboard.loading")}</span>
        </div>
      </div>
    );
  }

  const explorerPayload = explorerMe?.payload;
  const showExplorerEvents = false;

  return (
    <div className="w-100 pb-4">
      {showExplorerEvents && (
        <Alert
          variant="info"
          className="mb-4 rounded-4 border-0 shadow-sm"
        >
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <strong>Explorer</strong>
              {explorerPayload?.badge ? (
                <span className="badge bg-primary ms-2">{explorerPayload.badge}</span>
              ) : null}
              <p className="mb-0 small mt-1 text-secondary">
                {t("dashboard.explorerInfo")}
              </p>
            </div>
            <Button
              variant="primary"
              className="text-nowrap"
              onClick={() => navigate("/event-booking")}
            >
              {t("dashboard.viewEvents")}
            </Button>
          </div>
        </Alert>
      )}

      <div className="card shadow-lg border-0 pb-2 overflow-hidden">
        <div>
          <div
            className={`${
              selectedUser.BannerImage ? "" : "bg-project-gradient"
            } position-relative profile-banner d-flex justify-content-center align-items-center`}
            style={{
              backgroundImage: `url(${selectedUser?.BannerImage ?? ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              aspectRatio: "3 / 1",
            }}
          >
            {!selectedUser.BannerImage && (
              <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                <img
                  src="/SMBF-logo-white.png"
                  alt={t("dashboard.logoAlt")}
                  className="w-25 h-25"
                />
              </div>
            )}{" "}
            <div className="position-absolute translate-middle image-container">
              <div className="position-relative w-100 h-100">
                <Image
                  src={
                    selectedUser.ProfessionalPhoto ||
                    selectedUser.PersonalProfessionalPhoto ||
                    "/user.png"
                  }
                  roundedCircle
                  alt={t("dashboard.profilePhotoAlt")}
                  className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                />

                <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                  <CurvedTextOverlay
                    size={imageSize}
                    stroke={
                      isEarlyStageMember
                        ? "#18c2c6"
                        : isLegacyYouthMember
                        ? "#18c2c6"
                        : selectedUser?.membershipName === "Business Membership"
                        ? "#1c57b9"
                        : "#544bc1"
                    }
                    text={
                      isEarlyStageMember
                        ? "#EarlyStage"
                        : isLegacyYouthMember
                        ? "#Youth"
                        : selectedUser?.membershipName === "Business Membership"
                        ? "#Business"
                        : "#Executive"
                    }
                  />
                </span>
              </div>
            </div>
          </div>
          <div className="mx-3 pt-5 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <div className="mt-md-4 mt-lg-5">
                <h2 className="mb-0">{displayDashboardName}</h2>
                {displayDashboardRole && <span>{displayDashboardRole}</span>}
                {displayDashboardSector ? (
                  <div className="d-flex gap-1">
                    <span className="text-muted">{displayDashboardSector}</span>
                  </div>
                ) : null}
              </div>
              <div className="d-none d-md-block">
                {isLegacyYouthMember || isEarlyStageMember ? (
                  <div className="fw-semibold d-flex gap-1 align-items-center">
                    <FaUniversity size={25} />
                    {selectedUser.universityCollege}
                  </div>
                ) : (
                  <div className="d-flex gap-3 align-items-center cursor-pointer">
                    {selectedUser.businessLogo ? (
                      <img
                        src={selectedUser.BusinessLogo}
                        className="rounded-circle"
                        width={40}
                        height={40}
                        alt={t("dashboard.logoAlt")}
                      />
                    ) : (
                      <MdOutlineBusiness size={30} />
                    )}
                    <div className="fw-semibold">
                      {displayDashboardBusinessName}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        {cardData.map((card, index) => (
          <div
            style={{ minHeight: "20vh" }}
            className="col-12 col-md-4 mb-4"
            key={index}
          >
            <div className="card h-100 shadow-lg rounded-4 border-0 position-relative">
              <div className="card-body d-flex flex-column justify-content-between p-3">
                <div className="d-flex flex-row justify-content-between w-100">
                  <div>
                    <h5>{t(card.titleKey)}</h5>
                    <p className="card-text text-muted" style={{ whiteSpace: "pre-line" }}>{t(card.textKey)}</p>
                  </div>
                  {card.logo && (
                    <img src={card.logo} alt={t(card.titleKey)} height="30" />
                  )}
                </div>
                {card.titleKey === "dashboard.cards.muslimInvest.title" &&
                isYouthOrEarlyStageMembership(userData?.user?.membershipName) ? (
                  <Button className={DASHBOARD_CARD_BTN_CLASS} disabled>
                    {t("connections.upgradeToBusinessMembership")}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (card.titleKey === "dashboard.cards.profile.title") {
                        setShowProfileModal(true);
                      } else {
                        navigate(card.link);
                      }
                    }}
                    className={DASHBOARD_CARD_BTN_CLASS}
                    disabled={card.isComingSoon}
                  >
                    {card.isComingSoon
                      ? t("dashboard.comingSoon")
                      : card.titleKey === "dashboard.cards.profile.title"
                      ? t("dashboard.profilePreview")
                      : t("dashboard.goTo", { item: t(card.titleKey) })}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {showProfileModal && (
        <ProfileModal
          isConnected
          show={showProfileModal}
          onHide={() => setShowProfileModal(false)}
          selectedUser={{ ...userData?.user, ...userData?.imageUrls } as User}
        />
      )}
    </div>
  );
};

export default Dashboard;